import {InvestmentAccountService} from "@investmentAccountService";
import {toDeleteBlockedError} from "@/lib/errors/deleteBlockedError.ts";
import {PensionPlanAccount, PensionPlanMetadata} from "@/integrations/supabase/pension.models.ts";
import investmentService from "@investmentService";
import {supabase} from "@supabaseClient";
import metadataService from "@/services/metadata/MetadataService.ts";
import logger from "@logger";

/**
 * Service class for handling pension fund related operations
 */
class PensionFundsService extends InvestmentAccountService {

    async createPensionPlan(request: PensionPlanCreateRequest): Promise<PensionPlanAccount> {
        logger.info('Creating pension plan', request);
        const { userId, metadata, pensionType, newFundManagerName } = request;
        // Retrieve investment type based on pension type
        const investmentType = await investmentService.fetchPensionInvestmentType(pensionType);
        // TODO: we should either link this to a fund manager or delete it altogether
        const fundManager = newFundManagerName || request.fundManager || '';

        // Create investment account
        const investmentAccountData: InvestmentAccountCreateRequest = {
            userId: userId,
            investmentTypeId: investmentType.id,
            fundName: request.fundName,
            fundManager: fundManager,
            accountNumber: request.accountNumber,
            currency: request.currency,
            interestRate: request.interestRate,
            startingAmount: request.startingAmount,
            balance: request.startingAmount,
            notes: request.notes || null,
            description: request.description || null
        };

        const investmentAccount = await this.createInvestmentAccount(investmentAccountData);

        // If metadata has been provided, we handle the creation of pension metadata
        if (metadata) {
            logger.info('Metadata provided, creating metadata', metadata);
            try {
                // Create pension plan metadata
                await this.createPensionPlanMetadata(userId, investmentAccount.id, metadata, fundManager);
            } catch (error) {
                // Rollback: delete the investment account if metadata creation fails
                await supabase.from('investment_accounts').delete().eq('id', investmentAccount.id);
                await this.handleError(error);
                throw error;
            }
        }

        return investmentAccount;
    }

    /**
     * Updates a pension plan account for a user
     * @param request
     */
    async updatePensionPlan(request: PensionPlanUpdateRequest): Promise<PensionPlanAccount> {
        logger.info('Updating pension plan', request);
        const { metadata } = request;
        // Update investment account
        const updatedInvestmentAccount = await this.updateInvestmentAccount(request);

        if (metadata) {
            // Update pension plan metadata if type-specific fields are provided
            await this.updatePensionPlanMetadata(metadata);
        }

        return updatedInvestmentAccount
    }

    /**
     * Creates pension plan metadata based on the provided user details, request data, and optional new fund manager name.
     *
     * @param {string} userId - The ID of the user creating the pension plan metadata.
     * @param {string} investmentAccountId - The ID of the investment account associated with the pension plan.
     * @param {PensionPlanMetadataCreateRequest} request - The request object containing pension plan details including fund manager ID, pension type, and country.
     * @param {string | null} [newFundManagerName=null] - The name of the new fund manager to be created, applicable for government pensions only.
     * @return {Promise<PensionPlanMetadata>} A promise that resolves with the created PensionPlanMetadata object.
     */
    async createPensionPlanMetadata(userId: string, investmentAccountId: string, request: PensionPlanMetadataCreateRequest, newFundManagerName: string | null = null): Promise<PensionPlanMetadata> {
        logger.info(`Creating pension plan metadata for account: ${investmentAccountId}`, request);
        const { country } = request

        // Handle fund manager creation for government pension if new fund manager provided
        logger.info(`Creating new fund manager for pension: ${newFundManagerName}`);
        let fundManagerId = '';
        try {
            const existingFundManager = await metadataService.fetchFundManagerByName(userId, newFundManagerName);
            fundManagerId = existingFundManager.id;
        } catch (e) {
            const newFundManager = await metadataService.createFundManager({
                name: newFundManagerName,
                country: country,
                userId: userId,
            });
            fundManagerId = newFundManager.id;
        }

        const metadataData = this.convertRequestToSnakeCase({...request, fundManagerId, investmentAccountId});

        const { data, error } = await supabase
            .from('pension_plan_metadata')
            .insert(metadataData)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    async updatePensionPlanMetadata(request: PensionPlanMetadataUpdateRequest): Promise<PensionPlanMetadata> {
        logger.info('Updating pension plan metadata', request);
        const metadataData = this.convertRequestToSnakeCase(request);

        const { data, error } = await supabase
            .from('pension_plan_metadata')
            .update(metadataData)
            .eq('investment_account_id', request.investmentAccountId)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    async fetchPensionPlans(userId: string): Promise<PensionPlanAccount[]> {
        const pensionInvestmentTypes = await investmentService.fetchPensionInvestmentTypes();

        const pensionTypeIds = pensionInvestmentTypes.map(t => t.id);

        const { data, error } = await supabase
            .from('investment_accounts')
            .select(`
              *,
              investment_type:investment_types(*),
              pension_metadata:pension_plan_metadata(
                *,
                fund_manager:fund_managers(*)
              )`
            )
            .eq('user_id', userId)
            .in('investment_type_id', pensionTypeIds)
            .order('created_at', {ascending: false});

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Delete pension plan with a given ID for the provided user
     * @param {string} id Pension plan ID
     * @param {string} userId User ID
     */
    async deletePensionPlan(id: string, userId: string): Promise<void> {
        logger.warn(`Deleting pension plan ${id}`)
        const { error } = await supabase
            .from('investment_accounts')
            .delete()
            .eq('id', id)
            .eq("user_id", userId);

        if (error) {
            const reported = toDeleteBlockedError(error, "This pension plan has transactions, so it can't be deleted.");
            await this.handleError(reported as Error);
            throw reported;
        }
    }
}

export default new PensionFundsService();
